import { useContext, useEffect, useState } from 'react';
import { Link45Icon, SearchIcon } from '../svg';
import Model from '../components/common/models/Model';
import LinkDetailsCard from '../components/common/cards/LinkDetails';
import { UserContext } from '../context/UserContext';
import { ILink } from '../interfaces/Link';
import LinkShareCard from '../components/LinkShareCard/LinkShare';
import { BrandMemberRole, SortLinksByOptions } from '../types/enums';
import { toast } from 'react-toastify';
import LinkAndAppearanceEditCard from '../components/common/cards/EditLinkandAppearance';
import Input from '../components/common/Input';
import { Button } from '../components/common/Button';
import IconButton from '../components/common/IconButton';
import { useDebounce } from '../utils/useDebounce';
import LinkCardsView from './LinkCardsView';
import { useLocation, useNavigate } from 'react-router-dom';
import UpgradeRequiredModal from '../components/Modal/UpgradeRequiredModal';
import { AxiosError } from 'axios';
import useDeviceDetect from '../helpers/screens';
import useSubscription from '../hooks/useSubscription';
import Checkbox from '../components/common/Checkbox';
import TabNavigation from '../components/dashboard/TabNavigation';
import useI18n from '../hooks/useI18n';
import Loading from '../components/common/Loading';
import { generateSmartLink } from '@/utils/smartLinkUtils';
import {
  useUserLinksInfiniteQuery,
  useCreateLinkMutation,
  useDeleteLinksMutation,
} from '../hooks/queries/useLinkQueries';

const MyLinks: React.FC = () => {
  const { t } = useI18n();
  const { isMobile } = useDeviceDetect();
  const navigate = useNavigate();
  const location = useLocation();

  const [isDelete, setIsDelete] = useState(false);
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [copyLink, setCopyLink] = useState('');
  const [newLink, setNewLink] = useState('');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [edit, setEdit] = useState<ILink>({} as ILink);
  const [details, setDetails] = useState<ILink>({} as ILink);

  const tabs = [
    {
      label: String(t('myLinks.tabs.newlyAdded')),
      value: SortLinksByOptions.NewlyAdded,
    },
    {
      label: String(t('myLinks.tabs.topLinks')),
      value: SortLinksByOptions.TopLinks,
    },
    {
      label: String(t('myLinks.tabs.oldLinks')),
      value: SortLinksByOptions.OldLinks,
    },
    {
      label: String(t('myLinks.tabs.favourites')),
      value: SortLinksByOptions.Favourites,
    },
  ];
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [scrollToShortUrl, setScrollToShortUrl] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;
  const { hasSubscription } = useSubscription();
  const isConnectedWithYoutube = !!(user?.youtube && user?.youtube.accessToken);

  const {
    data: linksQueryData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
    refetch,
  } = useUserLinksInfiniteQuery(
    user?._id || '',
    tabs[selectedTab].value,
    debouncedSearchTerm,
  );

  const cardLinks: ILink[] =
    linksQueryData?.pages.flatMap((p: any) => {
      if (!p) return [];
      if (Array.isArray(p)) return p;
      return p.links ?? [];
    }) ?? [];

  const isPageLoading =
    !user?._id || isLoading || (isFetching && !isFetchingNextPage);

  const createLinkMutation = useCreateLinkMutation(user?._id || '');
  const deleteLinksMutation = useDeleteLinksMutation(user?._id || '');

  useEffect(() => {
    const navState = location.state as
      | {
          openSmartLinkDetailsModal?: boolean;
          smartLinkFromNotification?: ILink;
          source?: string;
          notificationType?: string;
        }
      | undefined;

    if (
      !navState?.openSmartLinkDetailsModal ||
      !navState?.smartLinkFromNotification
    ) {
      return;
    }

    setDetails(navState.smartLinkFromNotification);
    setIsDetailsModalOpen(true);

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const editModalOpen = (value: ILink) => {
    setIsEditModalOpen(true);
    setEdit(value);
  };

  const handleSelectLink = (value: string) => {
    setSelectedData((prev) => {
      if (prev.includes(value)) {
        return prev.filter((val) => val !== value);
      }
      return [...prev, value];
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedData([]);
    } else {
      const allIds = cardLinks.map((item) => item._id);
      setSelectedData(allIds);
      setSelectAll(true);
    }
  };

  const handleDeleteLinks = async () => {
    if (!user?._id) return;
    try {
      await deleteLinksMutation.mutateAsync(selectedData);
      toast.success(String(t('myLinks.deleteLinksSuccess')));
      setSelectedData([]);
      setIsDelete(false);
      setSelectAll(false);
    } catch (error) {
      toast.error(String(t('myLinks.deleteLinksError')));
    }
  };

  const detailsModelOpen = (id: string) => {
    setIsDetailsModalOpen(true);
    const link = cardLinks.find((link) => link._id === id);
    if (link) setDetails(link);
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(false);
    navigate('/plans');
  };

  const handleCreateLink = async () => {
    const urlRegex = new RegExp(
      '^(http://www.|https://www.|http://|https://)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/.*)?$',
    );
    if (!urlRegex.test(newLink)) {
      toast.error(String(t('errors.invalidUrl')));
      return;
    }
    if (user && user._id && newLink) {
      try {
        await createLinkMutation.mutateAsync(newLink);
        toast.success(String(t('myLinks.createLinkSuccess')));
        setNewLink('');
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsUpgradeModalOpen(true);
            return;
          }
          if (error.response?.status === 422) {
            toast.error(
              <div>
                {error.response.data.message} with{' '}
                <a
                  href="javascript:void(0)"
                  onClick={() =>
                    setScrollToShortUrl(error.response?.data.shortUrl)
                  }
                  className="text-blue-500 hover:underline"
                  aria-label={String(t('myLinks.scrollToLink'))}
                >
                  {error.response.data.shortUrl}
                </a>
              </div>,
              {
                autoClose: 5000,
              },
            );
            return;
          }
        }
        toast.error(String(t('errors.failedToCreateLink')));
      }
    }
  };

  const handleShareModalOpen = (id: string) => {
    setIsShareModalOpen(true);
    const link = cardLinks.find((link) => link._id === id);
    if (link) {
      const smartLink = generateSmartLink(link);
      setCopyLink(smartLink);
    }
  };

  const handleEditModalClose = () => setIsEditModalOpen(false);
  const handleDetailsModalClose = () => setIsDetailsModalOpen(false);
  const handleShareModalClose = () => setIsShareModalOpen(false);

  const displayForBrandUser = () => {
    return (
      user !== undefined &&
      user !== null &&
      user.brandMemberRole !== BrandMemberRole.User
    );
  };

  return (
    <div className="w-full pb-2 relative bg-white h-full">
      <div className="flex flex-col p-6 bg-[#F0F5FF]">
        <div>
          <h1 className="text-2xl font-header text-[#172B4D]">
            {String(t('myLinks.title'))}
          </h1>
        </div>
        {displayForBrandUser() && (
          <div className="flex flex-col md:flex-row items-center mt-[24px] ">
            <Input
              containerWrapperClass="w-full md:w-[432px] font-[Manrope]"
              placeholder={String(t('myLinks.pasteLinkHere'))}
              icon={<Link45Icon />}
              iconPosition="end"
              onChange={(e) => {
                setNewLink(e.target.value);
              }}
              value={newLink}
            />
            <Button
              onClick={handleCreateLink}
              className="lg:ml-4 max-md:mt-4 max-md:w-full"
              disabled={createLinkMutation.isPending}
            >
              {createLinkMutation.isPending
                ? String(t('dashboard.creating'))
                : String(t('myLinks.createSmartLink'))}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white p-6">
        {isPageLoading ? (
          <Loading
            centeredToParent={true}
            style={{
              top: 'calc(50% + 60px)',
            }}
          />
        ) : (
          <>
            {!isDelete ? (
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="border-b-2 flex items-center w-full md:w-[300px]">
                  <SearchIcon className={'size-5 text-gray-400'} />
                  <input
                    type="text"
                    placeholder={String(t('common.search'))}
                    name="search"
                    id="search"
                    onChange={handleSearch}
                    value={searchTerm}
                    className="border-none outline-none bg-transparent p-[10px]"
                  />
                </div>
                <div className="flex items-center gap-2 justify-end mt-[16px] md:mt-0">
                  <div className="">
                    <TabNavigation
                      tabs={tabs}
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                    />
                  </div>
                  {displayForBrandUser() && (
                    <IconButton
                      tooltip={String(t('common.delete'))}
                      src="/assets/svgs/fi_trash.svg"
                      className="size-50"
                      onClick={() => {
                        setIsDelete(true);
                      }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-between md:items-center flex-col-reverse md:flex-row w-full">
                <Checkbox
                  label={String(t('myLinks.selectAll'))}
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <div className="flex gap-2 w-full md:w-auto justify-end md:justify-end">
                  <Button onClick={() => setIsDelete(false)} variant="outlined">
                    {String(t('common.cancel'))}
                  </Button>
                  <Button
                    onClick={handleDeleteLinks}
                    disabled={selectedData?.length === 0}
                  >
                    {String(t('common.delete'))}
                  </Button>
                </div>
              </div>
            )}

            <LinkCardsView
              isDelete={isDelete}
              selectedData={selectedData}
              cardLinks={cardLinks}
              hasNextPage={hasNextPage ?? false}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              isError={isError}
              handleSelectLink={handleSelectLink}
              editModalOpen={editModalOpen}
              detailsModelOpen={detailsModelOpen}
              shareModalOpen={handleShareModalOpen}
              scrollToShortUrl={scrollToShortUrl}
              setScrollToShortUrl={setScrollToShortUrl}
            />
          </>
        )}
      </div>

      <Model
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        direction={isMobile ? 'bottom' : 'center'}
      >
        <LinkAndAppearanceEditCard
          link={edit}
          handleModalClose={handleEditModalClose}
          hasSubscription={hasSubscription || false}
          refetchLinks={async () => {
            await refetch();
          }}
        />
      </Model>

      <Model
        isOpen={isDetailsModalOpen}
        onClose={handleDetailsModalClose}
        direction={isMobile ? 'bottom' : 'center'}
      >
        <LinkDetailsCard
          data={details}
          handleDetailsModalClose={handleDetailsModalClose}
          hasSubscription={hasSubscription || false}
          isConnectedWithYoutube={isConnectedWithYoutube}
        />
      </Model>

      <Model isOpen={isShareModalOpen} onClose={handleShareModalClose}>
        <LinkShareCard
          handleShareModalClose={handleShareModalClose}
          link={copyLink}
        />
      </Model>

      <UpgradeRequiredModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onUpgrade={handleUpgradeClick}
      />
    </div>
  );
};

export default MyLinks;
